import { Box, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useMemo, useState } from 'react';
import { Page, PageTitleWithBack, Pagination, Table } from '../../../components';
import { getAlertStatuses, getBuilds, getBuildStatuses, restoreBuild } from '../../../fetch';
import {
  IBuild,
  IBuildStatus,
  IPeople,
  IFieldWalkthrough,
  IDropdownResponse
} from '../../../models';
import { ConvertBuildModal } from './ConvertBuildsModal';
import { DeleteBuildModal } from './DeleteBuildModal';
import { ManageBuildsFilters } from './ManageBuildsFilter';
import { MobileManageBuild } from './MobileManageBuild';
import { RegenerateTenDayAcceptanceModal } from './RegenerateTenDayAcceptanceModal';
import { ResetBuildModal } from './ResetBuildModal';
import { TestContractModal } from './TestContractModal';
import { ResetFieldWalkthroughModal } from './reset-fieldwalkthrough';
import { BuildActionsButton } from './actions-button';
import { TablePopover } from '../../../components/table/TablePopover';
import { formatShortFriendlyDateWithTime } from '../../../helpers';
import { getCommonPeople } from '../../../fetch/lookups';

export const ManageBuilds = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [builds, setBuilds] = useState<IBuild[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('NeighborhoodName');
  const [fieldWalkthroughs, setFieldWalkthroughs] = useState<IFieldWalkthrough[] | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | null>('');
  const [selectedLotId, setSelectedLotId] = useState<string | null>('');
  const [selectedJobType, setSelectedJobType] = useState<string>('');
  const [isLoadingStatuses, setLoadingStatuses] = useState<boolean>(false);
  const [isLoadingAgents, setLoadingAgents] = useState<boolean>(false);
  const [agents, setAgents] = useState<IPeople[] | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [statuses, setStatuses] = useState<IBuildStatus[] | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<string>('');
  const [filters, setFilters] = useState<{
    neighborhoodId?: number | string;
    lotNumber?: number | string;
    jobType?: string;
    status?:
      | 'New'
      | 'BuildApproved'
      | 'ContractAaccepted'
      | 'TenDayComplete'
      | 'Closing'
      | 'Complete'
      | string;
    agentId?: string;
    alertStatus?: string;
  } | null>({});
  const [sortDirection, setSortDirection] = useState<{
    NeighborhoodName?: 'Asc' | 'Desc';
    LotNumber?: 'Asc' | 'Desc';
    JobType?: 'Asc' | 'Desc';
    PlanDescription?: 'Asc' | 'Desc';
    State?: 'Asc' | 'Desc';
    City?: 'Asc' | 'Desc';
    Alert?: 'Asc' | 'Desc';
  }>({
    NeighborhoodName: 'Asc'
  });
  const [currentBuild, setBuild] = useState<IBuild | null>(null);
  const [isShowingCurrentModal, setIsShowingCurrentModal] = useState<{
    [id: string]: boolean;
  }>({});

  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchAgents = async () => {
    setLoadingAgents(true);
    try {
      const res = await getCommonPeople({
        perPage: '2147483647', // get all records
        personType: 'InternalAgent'
      });
      setAgents(res.records);
    } catch (error) {
      console.error(error);
    }
    setLoadingAgents(false);
  };

  const fetchOrderStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const res = await getBuildStatuses();
      setStatuses(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingStatuses(false);
  };

  const [isLoadingAlertStatuses, setLoadingAlertStatuses] = useState<boolean>(false);
  const [alertStatuses, setAlertStatuses] = useState<IDropdownResponse[] | null>(null);
  const fetchAlertStatuses = async () => {
    setLoadingAlertStatuses(true);
    try {
      const res = await getAlertStatuses();
      setAlertStatuses(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingAlertStatuses(false);
  };

  const fetchBuilds = async () => {
    setLoading(true);
    try {
      const res = await getBuilds({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      });
      setBuilds(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar(`Error loading builds, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderStatuses();
    fetchAgents();
    fetchAlertStatuses();
  }, []);

  useEffect(() => {
    fetchBuilds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, perPage, sortDirection, selectedSort]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleModalOpen = async (key: string, build: IBuild) => {
    setIsShowingCurrentModal({
      [key]: true
    });
    setBuild(build);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhood',
        isServerSorted: selectedSort === 'NeighborhoodName',
        isServerSortedDesc: sortDirection.NeighborhoodName === 'Desc',
        handleClickColumn: () => handleClickColumn('NeighborhoodName')
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Phase',
        accessor: 'phase',
        sort: false
      },
      {
        Header: 'Plan',
        accessor: 'planDescription',
        isServerSorted: selectedSort === 'PlanDescription',
        isServerSortedDesc: sortDirection.PlanDescription === 'Desc',
        handleClickColumn: () => handleClickColumn('PlanDescription')
      },
      {
        Header: 'Build Type',
        accessor: 'jobType',
        isServerSorted: selectedSort === 'JobType',
        isServerSortedDesc: sortDirection.JobType === 'Desc',
        handleClickColumn: () => handleClickColumn('JobType')
      },
      {
        Header: 'Buyer',
        accessor: 'buyer',
        sort: false
      },
      {
        Header: 'Status',
        accessor: 'status',
        sort: false,
        Cell: ({ cell }) => {
          const statusSelection =
            cell.row.original && cell.row.original.status && statuses
              ? statuses.find(s => s.value === cell.row.original.status)
              : null;

          if (statusSelection) {
            return <>{statusSelection.description}</>;
          }
          return <></>;
        }
      },
      {
        Header: 'Alert',
        accessor: 'alert',
        isServerSorted: selectedSort === 'Alert',
        isServerSortedDesc: sortDirection.Alert === 'Desc',
        handleClickColumn: () => handleClickColumn('Alert'),
        Cell: ({ cell }) => {
          return (
            <>
              {cell?.row?.original?.pendingDeletionAfter ? (
                <Popover
                  text={
                    cell?.row?.original?.pendingDeletionAfter
                      ? `Pending Deletion After: ${formatShortFriendlyDateWithTime(
                          cell?.row?.original?.pendingDeletionAfter
                        )}`
                      : ''
                  }
                >
                  {cell?.row?.original?.alert ?? ''}
                </Popover>
              ) : (
                cell?.row?.original?.alert ?? ''
              )}
            </>
          );
        }
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuild } };
        }) => {
          const selection = statuses?.find(s => s.value === original.status);
          const testContractDisabled =
            selection?.description === 'New' || selection?.description === '' ? true : false;

          return (
            <BuildActionsButton
              testContractDisabled={testContractDisabled}
              build={original}
              handleConvertToSpec={() => handleModalOpen('convert', original)}
              handleTestContract={() => handleModalOpen('test-contract', original)}
              regenerateTenDayAcceptance={() => handleModalOpen('regenerate', original)}
              handleResetFieldWalkthrough={(fieldWalkthroughs: IFieldWalkthrough[]) => {
                setFieldWalkthroughs(fieldWalkthroughs);
                handleModalOpen('reset-fieldwalkthrough', original);
              }}
              handleDelete={() => handleModalOpen('delete', original)}
              handleRestore={async buildId => {
                try {
                  await restoreBuild(buildId);
                  enqueueSnackbar(`Build restored!`, {
                    variant: 'success'
                  });
                  fetchBuilds();
                } catch (error) {
                  enqueueSnackbar('Error restoring build, please try again.', {
                    variant: 'error'
                  });
                }
              }}
              handleReset={() => handleModalOpen('reset-build', original)}
              fetchBuilds={fetchBuilds}
            />
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builds, fieldWalkthroughs]);

  return (
    <>
      <Page title="Build Utilities">
        <PageTitleWithBack backText="Back to Admin home" backUrl="/admin" title="Build Utilities" />
        <ManageBuildsFilters
          isLoading={isLoading}
          isLoadingAgents={isLoadingAgents}
          setSelectedAgent={setSelectedAgent}
          selectedAgent={selectedAgent}
          agents={agents}
          selectedStatus={selectedStatus}
          selectedNeighborhoodId={selectedNeighborhoodId}
          isLoadingStatuses={isLoadingStatuses}
          statuses={statuses}
          selectedLotId={selectedLotId}
          setSelectedNeighborhoodId={setSelectedNeighborhoodId}
          setSelectedLotId={setSelectedLotId}
          setSelectedStatus={setSelectedStatus}
          setSelectedJobType={setSelectedJobType}
          selectedJobType={selectedJobType}
          handleSearch={(clearSearch?: boolean) => {
            setPage(0);
            setFilters({
              neighborhoodId: selectedNeighborhoodId,
              status: selectedStatus as string,
              alertStatus: selectedAlert
            });
          }}
          applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
            if (clearFilters) {
              setFilters({
                neighborhoodId: '',
                lotNumber: '',
                jobType: '',
                status: '',
                agentId: '',
                alertStatus: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                neighborhoodId: selectedNeighborhoodId || undefined,
                lotNumber: selectedLotId || undefined,
                jobType: selectedJobType || undefined,
                status: selectedStatus || undefined,
                agentId: selectedAgent || undefined,
                alertStatus: selectedAlert || undefined
              });
            }
          }}
          selectedAlert={selectedAlert}
          setSelectedAlert={setSelectedAlert}
          alertStatuses={alertStatuses}
          isLoadingAlertStatuses={isLoadingAlertStatuses}
        />
        <Box marginTop="1rem">
          <Table
            columns={columns}
            data={builds}
            isLoading={isLoading}
            hidePagination
            ResponsiveComponent={isMobile ? MobileManageBuild : undefined}
            mobileProps={{
              handleConvertToSpec: (val: IBuild) => handleModalOpen('convert', val),
              regenerateTenDayAcceptance: (val: IBuild) => handleModalOpen('regenerate', val),
              handleTestContract: (val: IBuild) => handleModalOpen('test-contract', val),
              handleReset: (val: IBuild) => handleModalOpen('reset-build', val),
              handleDelete: (val: IBuild) => handleModalOpen('delete', val),
              handleResetFieldWalkthrough: (
                val: IBuild,
                fieldWalkthroughs: IFieldWalkthrough[]
              ) => {
                setFieldWalkthroughs(fieldWalkthroughs);
                handleModalOpen('reset-fieldwalkthrough', val);
              },
              statuses,
              fetchBuilds
            }}
          />
          {!isLoading && (
            <Pagination
              page={page}
              count={recordCount}
              rowsPerPage={perPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          )}
        </Box>
      </Page>
      <DeleteBuildModal
        open={isShowingCurrentModal?.['delete'] ?? false}
        onClose={() => {
          setIsShowingCurrentModal({});
          setBuild(null);
        }}
        currentBuild={currentBuild}
        fetchBuilds={fetchBuilds}
      />
      <ResetBuildModal
        open={isShowingCurrentModal?.['reset-build'] ?? false}
        onClose={() => {
          setIsShowingCurrentModal({});
          setBuild(null);
        }}
        currentBuild={currentBuild}
        fetchBuilds={fetchBuilds}
      />
      <ConvertBuildModal
        open={isShowingCurrentModal?.['convert'] ?? false}
        onClose={() => {
          setIsShowingCurrentModal({});
          setBuild(null);
        }}
        currentBuild={currentBuild}
        fetchBuilds={fetchBuilds}
      />
      <TestContractModal
        open={isShowingCurrentModal?.['test-contract'] ?? false}
        onClose={() => {
          setIsShowingCurrentModal({});
          setBuild(null);
        }}
        currentBuild={currentBuild}
        fetchBuilds={fetchBuilds}
      />
      <RegenerateTenDayAcceptanceModal
        open={isShowingCurrentModal?.['regenerate'] ?? false}
        onClose={() => {
          setIsShowingCurrentModal({});
          setBuild(null);
        }}
        currentBuild={currentBuild}
        fetchBuilds={fetchBuilds}
      />
      <ResetFieldWalkthroughModal
        open={isShowingCurrentModal?.['reset-fieldwalkthrough'] ?? false}
        onClose={() => {
          setIsShowingCurrentModal({});
          setBuild(null);
        }}
        fetchBuilds={fetchBuilds}
        fieldWalkthroughs={fieldWalkthroughs}
      />
    </>
  );
};

interface IPopover {
  text: string;
}
const Popover: FC<IPopover> = ({ text, children }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onPopperOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const onPopperClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <TablePopover greyBackground anchorEl={anchorEl} text={text} onPopperClose={onPopperClose} />
      <Box
        aria-owns={!!anchorEl ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={e => {
          onPopperOpen(e);
        }}
        onMouseLeave={onPopperClose}
      >{`${children ?? ''}`}</Box>
    </>
  );
};
